/**
 * 格式化基本类型的显示值
 */
function formatPrimitiveValue(value) {
  const type = typeof value;

  if (type === 'string') {
    return `"${value}"`;
  }
  if (type === 'number') {
    return String(value);
  }
  if (type === 'boolean') {
    return String(value);
  }
  if (value === undefined) {
    return 'undefined';
  }

  return String(value);
}

/**
 * 从对象中提取智能标签（用于数组元素展示）
 * @param {object} obj - 对象
 * @returns {string|null} 标签字符串或 null
 */
function extractSmartLabel(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return null;
  }

  // 优先级顺序：name > field > type > id > title > label
  const labelKeys = ['name', 'field', 'type', 'id', 'title', 'label'];

  for (const key of labelKeys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      const value = obj[key];
      // 如果值是字符串或数字，直接使用
      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }
    }
  }

  return null;
}

/**
 * 生成数组摘要（显示前几个元素的标签）
 * @param {Array} arr - 数组
 * @param {number} maxItems - 最多显示几个元素
 * @returns {string} 摘要字符串，如 "(project, user_type, short_id, ...)"
 */
function generateArraySummary(arr, maxItems = 3) {
  if (!Array.isArray(arr) || arr.length === 0) {
    return '';
  }

  const labels = [];
  for (let i = 0; i < Math.min(arr.length, maxItems); i++) {
    const item = arr[i];

    if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
      // 对象：尝试提取智能标签
      const label = extractSmartLabel(item);
      if (label) {
        labels.push(label);
      } else {
        // 没有智能标签，显示第一个字段的值
        const firstKey = Object.keys(item)[0];
        if (firstKey && item[firstKey] !== undefined) {
          const val = item[firstKey];
          if (typeof val === 'string' || typeof val === 'number') {
            labels.push(String(val));
          }
        }
      }
    } else if (typeof item === 'string' || typeof item === 'number') {
      // 基本类型：直接添加
      const str = String(item);
      labels.push(str.length > 20 ? str.substring(0, 20) + '...' : str);
    }
  }

  if (labels.length === 0) {
    return '';
  }

  const summary = labels.join(', ');
  const hasMore = arr.length > maxItems;
  return hasMore ? ` (${summary}, ...)` : ` (${summary})`;
}

/**
 * 尝试解析 JSON 字符串
 * @param {any} value - 要检查的值
 * @param {number} maxSize - 最大字符串大小（字节），默认 10KB
 * @returns {object|array|null} 解析成功返回对象/数组，失败返回 null
 */
function tryParseJSONString(value, maxSize = 10240) {
  // 1. 类型检查：只处理字符串
  if (typeof value !== 'string') {
    return null;
  }

  // 2. 大小限制：10KB
  if (value.length > maxSize) {
    return null;
  }

  // 3. 格式预判：必须以 { 或 [ 开头，以 } 或 ] 结尾
  const trimmed = value.trim();
  if (!(
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  )) {
    return null;
  }

  // 4. 尝试解析
  try {
    const parsed = JSON.parse(trimmed);
    // 确保解析结果是对象或数组（排除基本类型，如 JSON.parse('"string"')）
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
  } catch (e) {
    // 解析失败，静默返回 null
    return null;
  }

  return null;
}

/**
 * 将 JSON 对象转换为渲染树
 * @param {any} data - JSON 数据
 * @param {string} key - 当前节点的键名
 * @param {string} parentId - 父节点 ID
 * @param {number} level - 层级深度
 * @param {number} stringParseDepth - 当前字符串解析深度（限制嵌套解析）
 * @returns {RenderNode}
 */
export function buildRenderTree(data, key = 'root', parentId = '', level = 0, stringParseDepth = 0) {
  const id = parentId ? `${parentId}.${key}` : key;

  const node = {
    id,
    key,
    level,
    children: []
  };

  // 处理 null
  if (data === null) {
    return {
      ...node,
      type: 'null',
      value: null,
      displayValue: 'null',
      isExpandable: false,
      isExpanded: false
    };
  }

  // 处理数组
  if (Array.isArray(data)) {
    // 默认展开前两层（level 0 和 1）
    const shouldAutoExpand = level < 2;

    return {
      ...node,
      type: 'array',
      value: data,
      displayValue: data.length === 0 ? '[]' : '[...]', // 空数组显示 []，非空显示 [...]
      isExpandable: data.length > 0,
      isExpanded: shouldAutoExpand,
      children: data.map((item, index) => {
        // 统一使用索引作为 key
        return buildRenderTree(item, `[${index}]`, id, level + 1, stringParseDepth);
      })
    };
  }

  // 处理对象
  if (typeof data === 'object') {
    const keys = Object.keys(data);

    // 默认展开前两层（level 0 和 1）
    const shouldAutoExpand = level < 2;

    return {
      ...node,
      type: 'object',
      value: data,
      displayValue: keys.length === 0 ? '{}' : '{...}', // 空对象显示 {}，非空显示 {...}
      isExpandable: keys.length > 0,
      isExpanded: shouldAutoExpand,
      children: keys.map(k =>
        buildRenderTree(data[k], k, id, level + 1, stringParseDepth)
      )
    };
  }

  // 处理基本类型
  const valueType = typeof data;

  // 特殊处理：字符串类型尝试 JSON 解析（限制深度为 8 层，支持深度嵌套）
  if (valueType === 'string' && stringParseDepth < 8) {
    const parsed = tryParseJSONString(data);
    if (parsed !== null) {
      // 解析成功！递归构建子树
      const parsedNode = buildRenderTree(parsed, key, parentId, level, stringParseDepth + 1);

      // 标记此节点来自字符串解析，并保存原始字符串
      return {
        ...parsedNode,
        isFromStringParse: true,
        originalString: data,
        // 保留原始字符串的预览（前 100 字符）
        originalStringPreview: data.length > 100 ? data.substring(0, 100) + '...' : data
      };
    }
  }

  // 普通基本类型处理
  return {
    ...node,
    type: valueType, // 'string', 'number', 'boolean', 'undefined'
    value: data,
    displayValue: formatPrimitiveValue(data),
    isExpandable: false,
    isExpanded: false
  };
}

/**
 * 切换节点的展开/折叠状态
 * @param {RenderNode} tree - 渲染树
 * @param {string} nodeId - 要切换的节点 ID
 * @returns {RenderNode} 新的树（不可变更新）
 */
export function toggleNodeExpanded(tree, nodeId) {
  if (tree.id === nodeId) {
    return { ...tree, isExpanded: !tree.isExpanded };
  }

  if (tree.children && tree.children.length > 0) {
    return {
      ...tree,
      children: tree.children.map(child =>
        toggleNodeExpanded(child, nodeId)
      )
    };
  }

  return tree;
}

/**
 * 展开所有节点
 */
export function expandAll(tree) {
  const newTree = { ...tree, isExpanded: true };
  if (newTree.children && newTree.children.length > 0) {
    newTree.children = newTree.children.map(expandAll);
  }
  return newTree;
}

/**
 * 折叠所有节点
 */
export function collapseAll(tree) {
  const newTree = { ...tree, isExpanded: false };
  if (newTree.children && newTree.children.length > 0) {
    newTree.children = newTree.children.map(collapseAll);
  }
  return newTree;
}

/**
 * 检查数组是否适合表格视图（所有元素都是对象）
 * @param {Array} arr - 要检查的数组
 * @returns {boolean}
 */
export function isArrayTableViewable(arr) {
  if (!Array.isArray(arr) || arr.length === 0) {
    return false;
  }

  // 检查所有元素是否都是对象（非 null，非数组）
  return arr.every(item =>
    item !== null &&
    typeof item === 'object' &&
    !Array.isArray(item)
  );
}

/**
 * 获取数组中所有对象的键（合并）
 * @param {Array} arr - 对象数组
 * @returns {string[]} 所有唯一的键
 */
export function getArrayTableColumns(arr) {
  const keysSet = new Set();

  arr.forEach(item => {
    if (item && typeof item === 'object') {
      Object.keys(item).forEach(key => keysSet.add(key));
    }
  });

  return Array.from(keysSet);
}
